/**
 * The building blocks of a conversation (V2Conversation, docs/design/APP-APPLE.md §6): day separators, groups of
 * bubbles (consecutive messages of one author less than 3 minutes apart: 3 px apart inside a group, 14 px between
 * groups, the last bubble's bottom corner on the author's side cut to 6 px), the agent's character beside the last
 * bubble of each of its groups, the reactions as badges attached to the bubble's top corner (opposite to the author),
 * the reaction picker, the hover actions (react, reply) and the long-press menu (Répondre, Copier, Retenir), the
 * typing dots and the reply bar of the composer. Never an emoji on screen: the six reactions are drawn glyphs.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { ChatMessage } from "../../shared/contracts";
import { WEB_REACTIONS } from "../../shared/contracts";
import { AgentAvatarView, type AvatarAgent, type CharacterMood } from "./avatar";
import { IconBookmark, IconCopyPlain, IconHeartFill, IconReply, IconSmile, IconThumbFill, IconX } from "./icons";
import { cx } from "./ui";

export type ReactionEmoji = (typeof WEB_REACTIONS)[number];

/** The six reactions, in the picker's order: their label (« Adore »…) and how they are drawn. */
export const REACTIONS: { emoji: ReactionEmoji; label: string; text?: string }[] = [
  { emoji: "❤️", label: "Adore" },
  { emoji: "👍", label: "Bien" },
  { emoji: "👎", label: "Bof" },
  { emoji: "😂", label: "Drôle", text: "HA" },
  { emoji: "‼️", label: "Important", text: "!!" },
  { emoji: "❓", label: "Question", text: "?" },
];

/** The label of any tapback (an iMessage emoji tapback outside the six reads « Réaction »). */
export function reactionLabel(emoji: string): string {
  return REACTIONS.find((r) => r.emoji === emoji || r.emoji.replace(/️/g, "") === emoji.replace(/️/g, ""))?.label ?? "Réaction";
}

function normalized(emoji: string): string {
  return emoji.replace(/️/g, "");
}

/** One reaction's glyph: a heart (pink), a thumb (up or down), « HA », « !! », « ? », or a smile for any other emoji. */
export function ReactionGlyph({ emoji, size, dark = false }: { emoji: string; size: number; dark?: boolean }) {
  const e = normalized(emoji);
  if (e === normalized("❤️")) return <IconHeartFill width={size} height={size} className="text-heart" />;
  if (e === "👍") return <IconThumbFill width={size} height={size} />;
  if (e === "👎") return <IconThumbFill width={size} height={size} style={{ transform: "rotate(180deg)" }} />;
  const r = REACTIONS.find((x) => normalized(x.emoji) === e);
  if (r?.text) return <span className={cx("leading-none font-bold", dark ? "text-ink-fg" : "")} style={{ fontSize: size * 0.72 }}>{r.text}</span>;
  return <IconSmile width={size} height={size} />;
}

// ───────────── Reactions grouped on their target ─────────────

/** The current tapback of each author on each line: the newest one wins, a removal clears it. */
export type Reactions = Map<string, { me?: string; agent?: string; at: number }>;

export function reactionsOf(messages: readonly ChatMessage[], local: Readonly<Record<string, string | null>> = {}): Reactions {
  const out: Reactions = new Map();
  for (const m of messages) {
    const r = m.reaction;
    if (!r?.targetId) continue;
    const who = r.byAgent ? "agent" : "me";
    const cur = out.get(r.targetId) ?? { at: 0 };
    if (r.removed) {
      if (cur[who] && normalized(cur[who]!) === normalized(r.emoji)) delete cur[who];
    } else {
      cur[who] = r.emoji;
      cur.at = m.createdAt;
    }
    out.set(r.targetId, cur);
  }
  // The owner's own taps, until the server's line arrives (null: taken back).
  for (const [target, emoji] of Object.entries(local)) {
    const cur = out.get(target) ?? { at: Date.now() };
    if (emoji) cur.me = emoji;
    else delete cur.me;
    out.set(target, cur);
  }
  return out;
}

/**
 * The badges of a bubble: 28 px discs on the bubble's top corner opposite to its author (top right of the agent's
 * bubble, top left of the owner's), a 3 px ring of the background cutting into the bubble; several overlap by 10 px.
 */
export function ReactionBadges({ me, agent, side, agentName, pop }: { me?: string; agent?: string; side: "left" | "right"; agentName: string; pop: boolean }) {
  const list = [
    ...(me ? [{ emoji: me, by: "toi" }] : []),
    ...(agent ? [{ emoji: agent, by: agentName }] : []),
  ];
  if (!list.length) return null;
  return (
    <span className={cx("absolute -top-3 z-[2] flex", side === "right" ? "-right-3 flex-row-reverse" : "-left-3")}>
      {list.map((r, i) => (
        <span
          key={`${r.by}-${r.emoji}`}
          data-testid="msg-reaction"
          role="img"
          aria-label={`${reactionLabel(r.emoji)}, par ${r.by}`}
          className={cx(
            "flex h-7 w-7 items-center justify-center rounded-full bg-bubble-user text-fg shadow-[0_0_0_3px_var(--bg)]",
            pop && "cv-pop",
            i > 0 && (side === "right" ? "-mr-2.5" : "-ml-2.5"),
          )}
        >
          <ReactionGlyph emoji={r.emoji} size={14} />
        </span>
      ))}
    </span>
  );
}

/** The picker over a bubble (outer side): six options, the one already set in light (its heart stays pink). */
export function ReactionPicker({ current, side, onPick, large = false }: { current?: string; side: "left" | "right"; onPick: (emoji: ReactionEmoji) => void; large?: boolean }) {
  return (
    <div
      role="group"
      aria-label="Réactions"
      className={cx(
        "cv-picker absolute bottom-[calc(100%+8px)] z-[5] flex gap-0.5 rounded-full bg-chip p-1 shadow-[0_0_0_1px_var(--border-strong),0_12px_32px_rgb(0_0_0/0.55)]",
        side === "left" ? "left-0 origin-bottom-left" : "right-0 origin-bottom-right",
      )}
    >
      {REACTIONS.map((r) => {
        const on = current !== undefined && normalized(current) === normalized(r.emoji);
        return (
          <button
            key={r.emoji}
            type="button"
            aria-label={r.label}
            aria-pressed={on}
            onClick={(e) => {
              e.stopPropagation();
              onPick(r.emoji);
            }}
            className={cx(
              "cv-opt flex items-center justify-center rounded-full",
              large ? "h-11 w-11" : "h-9 w-9",
              on ? "bg-ink text-ink-fg" : "text-fg hover:bg-line-strong",
            )}
          >
            <ReactionGlyph emoji={r.emoji} size={large ? 19 : 17} dark={on} />
          </button>
        );
      })}
    </div>
  );
}

// ───────────── Days and groups ─────────────

const DAY = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const DAY_YEAR = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const TIME = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });

export function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** « Aujourd'hui », « Hier », « lundi 2 novembre » (with the year when it is not this one). */
export function dayLabel(ms: number, now: number): string {
  const diff = Math.round((startOfDay(now) - startOfDay(ms)) / 86_400_000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Hier";
  return (new Date(ms).getFullYear() === new Date(now).getFullYear() ? DAY : DAY_YEAR).format(ms);
}

export function timeLabel(ms: number): string {
  return TIME.format(ms);
}

export function DaySeparator({ label }: { label: string }) {
  return <p className="pt-3.5 pb-1 text-center text-[12.5px] text-subtle first:pt-1">{label}</p>;
}

/** Consecutive messages of one author less than 3 minutes apart (and the same day) form a group. */
export const GROUP_MS = 3 * 60_000;

export function sameGroup(a: { out: boolean; at: number } | null, b: { out: boolean; at: number }): boolean {
  return !!a && a.out === b.out && b.at - a.at < GROUP_MS && startOfDay(a.at) === startOfDay(b.at);
}

/** The bubble's corners: 20 px, the last of a group cut to 6 px at the bottom on the author's side. */
export function bubbleRadius(out: boolean, last: boolean): string {
  if (!last) return "rounded-[20px]";
  return out ? "rounded-[20px_20px_20px_6px]" : "rounded-[20px_20px_6px_20px]";
}

/** Text size of the bubbles: 16 px on a phone, 15.5 px from 761 px; line height 1.45; 10 × 14 px inside. */
export const BUBBLE_TEXT = "px-3.5 py-2.5 text-[16px] leading-[1.45] md:text-[15.5px]";

// ───────────── One row ─────────────

/**
 * One row of the thread: the agent's (left; its character beside the last bubble of a group) or the owner's
 * (right). `aside` holds what sits beside the bubble (the hover actions); `below` what goes under it (Oui / Non).
 */
export function Row({
  out,
  first,
  last,
  agent,
  mood,
  testId,
  id,
  hasReactions,
  fresh,
  lifted,
  wide,
  bubble,
  aside,
  below,
  onPointerDown,
  className,
}: {
  out: boolean;
  first: boolean;
  last: boolean;
  agent: AvatarAgent;
  mood?: CharacterMood;
  testId?: string;
  id?: string;
  hasReactions?: boolean;
  fresh?: boolean;
  lifted?: boolean;
  /** A generated interface: the bubble takes the whole width it may have. */
  wide?: boolean;
  bubble: ReactNode;
  aside?: ReactNode;
  below?: ReactNode;
  onPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
  className?: string;
}) {
  const marginTop = (first ? 14 : 3) + (hasReactions ? 12 : 0);
  return (
    <div
      data-testid={testId}
      id={id}
      className={cx("cv-row group/row relative flex items-end gap-2", out ? "justify-start" : "justify-end", fresh && "cv-in", lifted && "z-50", className)}
      style={{ marginTop } as CSSProperties}
    >
      {out ? (
        <div className="w-[26px] shrink-0 self-end">{last ? <AgentAvatarView agent={agent} size={26} bare mood={mood} /> : null}</div>
      ) : null}
      <div className={cx("flex min-w-0 items-center gap-1.5 max-md:max-w-[82%] md:max-w-[75%]", out ? "flex-row" : "flex-row-reverse", wide && "w-full")}>
        <div className={cx("flex min-w-0 flex-col gap-2", out ? "items-start" : "items-end", wide && "w-full")} onPointerDown={onPointerDown}>
          {bubble}
          {below}
        </div>
        {aside}
      </div>
    </div>
  );
}

/** The two round buttons beside a bubble on hover (desktop): « Réagir » (the agent's bubbles) and « Répondre », and the time. */
export function HoverActions({ out, time, onReact, onReply, pickerOpen }: { out: boolean; time: string; onReact?: () => void; onReply: () => void; pickerOpen?: boolean }) {
  const btn =
    "flex h-7 w-7 items-center justify-center rounded-full bg-bubble-agent text-muted shadow-[inset_0_0_0_1px_var(--border)] transition hover:text-fg";
  return (
    <div
      className={cx(
        "flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-150 group-hover/row:opacity-100 focus-within:opacity-100 max-md:hidden [@media(hover:none)]:hidden",
        out ? "flex-row" : "flex-row-reverse",
        pickerOpen && "opacity-100",
      )}
    >
      {onReact ? (
        <button type="button" aria-label="Réagir" aria-expanded={pickerOpen} onClick={onReact} className={btn}>
          <IconSmile width={15} height={15} strokeWidth={2} />
        </button>
      ) : null}
      <button type="button" aria-label="Répondre" onClick={onReply} className={btn}>
        <IconReply width={15} height={15} strokeWidth={2} />
      </button>
      <span className="px-1 text-[12px] whitespace-nowrap text-subtle">{time}</span>
    </div>
  );
}

/** A quoted line, inside the reply's bubble (top): who wrote it and its first words; a tap scrolls back to it. */
export function QuoteInset({ who, text, onJump }: { who: string; text: string; onJump?: () => void }) {
  return (
    <button
      type="button"
      data-testid="msg-quote"
      onClick={onJump}
      aria-label={`Voir le message cité : ${who}`}
      className="mb-1.5 block w-full min-w-0 rounded-[12px] bg-inset px-2.5 py-[7px] text-left"
    >
      <span className="block text-[12.5px] font-semibold text-accent">{who}</span>
      <span className="line-clamp-2 block text-[13.5px] leading-snug text-muted wrap-anywhere">{text}</span>
    </button>
  );
}

/** Three 6 px dots that bounce (0.15 s apart, 1.2 s). */
export function TypingDots({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-[5px] py-1" role="status" aria-label={label}>
      <span className="cv-dot" />
      <span className="cv-dot" />
      <span className="cv-dot" />
    </span>
  );
}

/** Scrolls to a line and lights it up for a moment (a tap on a quote). */
export function flashLine(id: string): void {
  const row = document.getElementById(`msg-${id}`);
  if (!row) return;
  row.scrollIntoView({ block: "center", behavior: "smooth" });
  const bubble = row.querySelector<HTMLElement>("[data-bubble]");
  if (!bubble) return;
  bubble.classList.remove("cv-flash");
  void bubble.offsetWidth;
  bubble.classList.add("cv-flash");
}

// ───────────── Long press (touch) ─────────────

/** A long press (450 ms without moving) on a touch screen; the context menu of the system never opens. */
export function useLongPress(onPress: () => void): (e: React.PointerEvent<HTMLDivElement>) => void {
  const latest = useRef(onPress);
  latest.current = onPress;
  return (e) => {
    if (e.pointerType !== "touch") return;
    const x = e.clientX;
    const y = e.clientY;
    const target = e.currentTarget;
    const timer = setTimeout(() => {
      cleanup();
      navigator.vibrate?.(8);
      latest.current();
    }, 450);
    const move = (ev: PointerEvent) => {
      if (Math.hypot(ev.clientX - x, ev.clientY - y) > 8) cleanup();
    };
    const cleanup = () => {
      clearTimeout(timer);
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", cleanup);
      target.removeEventListener("pointercancel", cleanup);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", cleanup);
    target.addEventListener("pointercancel", cleanup);
  };
}

/** The menu under a long-pressed bubble: « Répondre », « Copier », « Retenir ». */
export function PressMenu({ side, onReply, onCopy, onRemember }: { side: "left" | "right"; onReply: () => void; onCopy: () => void; onRemember?: () => void }) {
  const item = "flex h-11 w-full items-center justify-between gap-3 px-4 text-left text-[15px] text-fg";
  return (
    <div
      role="menu"
      className={cx("cv-picker absolute top-[calc(100%+10px)] z-[5] w-[220px] overflow-hidden rounded-[14px] bg-bubble-agent shadow-[0_14px_34px_rgb(0_0_0/0.6)]", side === "left" ? "left-0 origin-top-left" : "right-0 origin-top-right")}
    >
      <button type="button" role="menuitem" onClick={onReply} className={cx(item, "border-b border-line")}>
        Répondre
        <IconReply width={17} height={17} strokeWidth={2} />
      </button>
      <button type="button" role="menuitem" onClick={onCopy} className={cx(item, onRemember && "border-b border-line")}>
        Copier
        <IconCopyPlain width={17} height={17} strokeWidth={2} />
      </button>
      {onRemember ? (
        <button type="button" role="menuitem" onClick={onRemember} className={item}>
          Retenir
          <IconBookmark width={17} height={17} strokeWidth={2} />
        </button>
      ) : null}
    </div>
  );
}

/** The dim veil behind a long-press menu (a tap closes it). */
export function PressVeil({ onClose }: { onClose: () => void }) {
  return <button type="button" aria-label="Fermer" onClick={onClose} className="fixed inset-0 z-40 h-full w-full cursor-default bg-[rgb(0_0_0/0.6)]" />;
}

/** Closes a popover on a click elsewhere and on Escape. */
export function useDismiss(open: boolean, onClose: () => void): void {
  const latest = useRef(onClose);
  latest.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") latest.current();
    };
    const onDown = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest?.("[data-popover]")) latest.current();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [open]);
}

// ───────────── The composer's reply bar ─────────────

/** Above the input, in the same container: the arrow, « Réponse à Nova », the start of the quoted line, ×. */
export function ReplyBar({ who, text, onCancel }: { who: string; text: string; onCancel: () => void }) {
  return (
    <div className="cv-bar flex items-center gap-2.5 border-b border-line py-2 pr-2 pl-4">
      <IconReply width={16} height={16} strokeWidth={2} className="shrink-0 text-accent" />
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold text-accent">Réponse à {who}</span>
        <span className="block truncate text-[13px] text-muted">{text}</span>
      </span>
      <button type="button" aria-label="Annuler la réponse" onClick={onCancel} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-chip text-muted max-md:h-11 max-md:w-11">
        <IconX width={12} height={12} strokeWidth={2.6} />
      </button>
    </div>
  );
}

/** A short-lived line at the bottom (« Copié », « Retenu »). */
export function useToast(): [string | null, (text: string) => void] {
  const [toast, setToast] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return [
    toast,
    (text: string) => {
      setToast(text);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setToast(null), 2200);
    },
  ];
}
